/**
 * `<TemplateDegradedBanner>` — slim amber persistent fallback for the
 * C2 banner band when the workspace's snapshot reports
 * `templateCompatible === false` (Crucible #790, sliced from #782
 * defect 4).
 *
 * Mounting story. The C2 banner band has two existing banners:
 *
 *   1. `<TemplateTooOldBanner>` — the big dismissable amber warning.
 *      Stops rendering once the user clicks the X (persisted via
 *      `activity-prefs:save`).
 *   2. `<ActivityBanner>` — the live activity strip. Already short-
 *      circuits to `null` when `templateCompatible === false` because
 *      the marker parser is skipped and its microcopy would be
 *      misleading.
 *
 * Result before this issue: once the user dismissed `<TemplateTooOldBanner>`,
 * the C2 band rendered nothing — empty space above the chat surface,
 * no positive signal that live activity is unavailable. This
 * component is the persistent slim fallback that takes over from
 * `<TemplateTooOldBanner>` post-dismiss.
 *
 * Visibility rules:
 *   - `snapshot === null` → null (no build observed yet; nothing to
 *     warn about).
 *   - `snapshot.templateCompatible !== false` → null.
 *   - Prefs query unresolved (`data === undefined`) → null (mirrors
 *     `<TemplateTooOldBanner>`'s no-flash guard so the slim fallback
 *     doesn't briefly appear before the persisted dismissal lands).
 *   - Prefs say NOT dismissed → null (the big `<TemplateTooOldBanner>`
 *     covers this window; the two are mutually exclusive).
 *   - Prefs say dismissed → render. This is the only state where the
 *     slim band visibly mounts.
 *
 * Visual spec — single-row slim band, distinct from the big
 * dismissable banner:
 *
 *   ⚠ Activity banner needs template v0.21.0+
 *
 *   | Container | `<section role="status" aria-label="Activity banner unavailable">` |
 *   | Background | `bg-amber-500/[0.06]` (lighter than the big banner)  |
 *   | Border     | `border-b border-amber-400/15` (slim divider)        |
 *   | Padding    | `px-4 py-1` (single-row height)                      |
 *   | Icon       | 12px `AlertTriangle` `text-amber-300/80`             |
 *   | Copy       | `text-xs text-amber-100/80`                          |
 *
 * The slim band is intentionally quieter than the big banner — it
 * is the always-on residual after the user has actively chosen to
 * dismiss the louder warning. Loud enough to inform; quiet enough
 * to not nag.
 *
 * Flag-gate. Defence in depth — the centre-column shell (#671) is
 * the canonical mount-gate; the prefs hook is also gated on the
 * flag so a `CRUCIBLE_AGENT_TRANSPARENCY=0` user never reads the
 * sidecar. Mirrors `<TemplateTooOldBanner>` so the two banners stay
 * in lock-step.
 *
 * British spelling throughout (behaviour, colour, centre).
 */

import { AlertTriangle } from 'lucide-react';
import type { ActivitySnapshot } from '@volley/crucible-ipc-types/ipc-channels';
import { useActivityPrefs } from '../hooks/useActivityPrefs';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

export function TemplateDegradedBanner({
  gameId,
  snapshot,
}: {
  gameId: string;
  snapshot: ActivitySnapshot | null;
}): React.JSX.Element | null {
  const flags = useFeatureFlags();
  // Gate the prefs IPC on the flag too — a user running with the
  // kill switch off should never hit the sidecar. The hook still
  // has to be invoked on every render to satisfy the Rules of
  // Hooks; `enabled: false` is the production-equivalent of a
  // no-op.
  const flagOn = flags.loaded && flags.agentTransparency;
  const { data } = useActivityPrefs(gameId, { enabled: flagOn });

  // 0. Feature-flag gate. Defence in depth — the C2 shell (#671)
  //    also gates the mount, but the component MUST be safe if a
  //    future caller mounts it without the parent gate.
  if (!flagOn) return null;

  // 1. No snapshot yet — nothing to warn about.
  if (snapshot === null) return null;

  // 2. Template is compatible — slim fallback unnecessary.
  if (snapshot.templateCompatible !== false) return null;

  // 3. Prefs still loading — hold off to avoid a flash on
  //    remount when the user has previously NOT dismissed (data
  //    === undefined on first paint).
  if (data === undefined) return null;

  // 4. Prefs say NOT dismissed — `<TemplateTooOldBanner>` is
  //    handling this window. The two banners are mutually
  //    exclusive: the big one carries the same root cause and
  //    the dismiss control; the slim one only takes over after
  //    that's been actioned. `data === null` is the "no sidecar
  //    on disk yet" state, which is treated as "not dismissed"
  //    so the big banner gets first refusal.
  if (data === null || !data.templateWarningDismissed) return null;

  return (
    <section
      role="status"
      aria-label="Activity banner unavailable"
      data-testid="template-degraded-banner"
      className="flex items-center gap-2 border-b border-amber-400/15 bg-amber-500/[0.06] px-4 py-1"
    >
      <AlertTriangle
        className="h-3 w-3 flex-none text-amber-300/80"
        aria-hidden="true"
        data-testid="template-degraded-banner-icon"
      />
      <span className="text-xs text-amber-100/80" data-testid="template-degraded-banner-copy">
        Activity banner needs template v0.21.0+
      </span>
    </section>
  );
}
